import { ReadingStatus } from 'infrastructure/enums/reading-statuses';
import { ReadingType } from 'infrastructure/enums';

import type { IPatientThresholds } from 'infrastructure/interfaces';
import type { Nullable } from 'infrastructure/types';
import type {
  IActivityData,
  StatusKeys,
  TThresholdName,
} from 'infrastructure/interfaces/i-rpm-patient-activity';

const readingsUnits: Record<StatusKeys, string> = {
  glucose: 'mg/dL',
  systolic: 'mmHg',
  diastolic: 'mmHg',
  pulse: 'bpm',
  weight: 'lbs',
  spo2: '%',
  fev1: 'L',
  pef: 'L/min',
  temperature: '°F',
  oxygen: '%',
};

export const hiddenThresholds = [
  'duration',
  'rem',
  'respiration',
  'bodyTurnOver',
  'lightSleep',
  'deepSleep',
  'bedTime',
  'getUpTime',
  'unit',
  'diastolic',
  'readingPeriod',
];

const thresholdPresenter = (
  threshold: StatusKeys,
  readingType: ReadingType,
): string | undefined => {
  const isSleepingMat =
    readingType === ReadingType.SleepingMat ||
    readingType === ReadingType.SleepingMatDuration ||
    readingType === ReadingType.SleepingMatActivity;

  const map: Partial<Record<StatusKeys, string>> = {
    pef: 'PEF',
    fev1: 'FEV1',
    systolic: 'Blood Pressure',
    pulse: isSleepingMat ? 'Resting Pulse' : threshold,
  };

  return map[threshold];
};

export const getReadingUnit = (threshold: StatusKeys) =>
  readingsUnits[threshold] || '';

const getBloodPressureStatus = (
  systolic: ReadingStatus,
  diastolic: ReadingStatus,
): ReadingStatus => {
  if (
    systolic === ReadingStatus.Critical ||
    diastolic === ReadingStatus.Critical
  ) {
    return ReadingStatus.Critical;
  }
  if (systolic === ReadingStatus.Risk || diastolic === ReadingStatus.Risk) {
    return ReadingStatus.Risk;
  }
  return ReadingStatus.Normal;
};

type TReadingInfo = {
  key: string;
  value: string;
  unit: string;
  className: string;
};

export const getReadingInfo = (
  statuses: IActivityData['statuses'],
  value: IActivityData['value'],
  threshold: StatusKeys,
  readingType: ReadingType,
): TReadingInfo => {
  const result = {
    key: thresholdPresenter(threshold, readingType) ?? threshold,
    value: value[threshold].toString(),
    unit: getReadingUnit(threshold),
    className: (statuses as Record<StatusKeys, ReadingStatus>)[threshold],
  };

  if (threshold === 'systolic' && threshold in statuses) {
    result.value = `${value.systolic}/${value.diastolic}`;
    result.className = getBloodPressureStatus(
      statuses.systolic,
      statuses.diastolic,
    );
  }

  if (threshold === 'spo2' && 'oxygen' in statuses) {
    result.className = statuses.oxygen;
  }

  return result;
};

export const isCHFReading = (thresholdName?: Nullable<TThresholdName>) => {
  if (
    thresholdName === 'chfDailyWeightIncrease' ||
    thresholdName === 'chfFiveDayWeightIncrease'
  ) {
    return true;
  }
  return false;
};

export const getWeightRiskContent = (
  thresholds: IPatientThresholds,
  thresholdName?: Nullable<TThresholdName>,
) => {
  if (!thresholdName) return '';

  const weeklyValue = thresholds[thresholdName];
  const dailyValue = thresholds[thresholdName];
  const maxCriticalValue = thresholds[thresholdName];
  const maxRiskValue = thresholds[thresholdName];
  const minCriticalValue = thresholds[thresholdName];
  const minRiskValue = thresholds[thresholdName];

  const contentMap: Partial<Record<TThresholdName, string>> = {
    chfFiveDayWeightIncrease: `Weekly Increase Limit ≥ ${weeklyValue} lbs`,
    chfDailyWeightIncrease: `Daily Increase Limit ≥ ${dailyValue} lbs`,
    maxRiskWeightChange: `At Risk Increase ≥ ${maxRiskValue} lbs`,
    minRiskWeightChange: `At Risk Decrease ≥ ${minRiskValue} lbs`,
    maxCriticalWeightChange: `Critical Increase ≥ ${maxCriticalValue} lbs`,
    minCriticalWeightChange: `Critical Decrease ≥ ${minCriticalValue} lbs`,
  };

  return contentMap[thresholdName];
};
